import jsPDF from 'jspdf'
import type { Appointment, VisitResult } from '@lib/supabase/appointment.service'
import type { Patient } from '@lib/supabase/patientAuth.service'
import type { Provider } from '@lib/supabase/provider.service'

const TEMPLATE_PATH = '/Prescription Template.png'

const BRAND_GREEN = '#176B59'
const BRAND_DARK = '#12312B'
const MUTED_GREEN = '#72817C'

function addWrappedText(
  pdf: jsPDF,
  text: string,
  x: number,
  y: number,
  width: number,
  lineHeight = 5,
) {
  const lines = pdf.splitTextToSize(text || 'Not provided', width) as string[]

  pdf.text(lines, x, y)

  return y + lines.length * lineHeight
}

async function imageToDataUrl(path: string): Promise<string> {
  const response = await fetch(path)

  if (!response.ok) {
    throw new Error(`Failed to load image: ${path}`)
  }

  const blob = await response.blob()

  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()

    reader.onloadend = () => {
      resolve(String(reader.result))
    }

    reader.onerror = () => {
      reject(new Error(`Failed to convert image to data URL: ${path}`))
    }

    reader.readAsDataURL(blob)
  })
}

export async function downloadVisitResultPdf({
  appointment,
  result,
  patient,
  provider,
}: {
  appointment: Appointment
  result: VisitResult
  patient: Patient
  provider: Provider | null
}) {
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
  })

  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()

  /*
   * ---------------------------------------------------------
   * LOAD THE APPROVED HOMICAREPLUS PRESCRIPTION TEMPLATE
   * ---------------------------------------------------------
   *
   * The PNG already contains:
   * - HomiCareplus logo
   * - "Professional Healthcare at Home"
   * - green header bar
   * - Africa watermark
   * - prescription disclaimer
   * - footer
   * - website / phone number
   * - QR code
   *
   * Therefore we DO NOT recreate any of these elements here.
   */
  const templateDataUrl = await imageToDataUrl(TEMPLATE_PATH)

  /*
   * The supplied artwork is 1000 × 1400.
   * We intentionally stretch it to the full A4 page so the
   * visual design remains exactly aligned with the supplied
   * template.
   */
  pdf.addImage(
    templateDataUrl,
    'PNG',
    0,
    0,
    pageWidth,
    pageHeight,
  )

  /*
   * ---------------------------------------------------------
   * DYNAMIC CONTENT AREA
   * ---------------------------------------------------------
   *
   * The reference template has a large blank area between
   * the header and the disclaimer.
   *
   * Keep all dynamic medical information inside that area.
   */

  const margin = 22
  const contentWidth = pageWidth - margin * 2

  // Approximate position corresponding to the blank area
  // in the supplied 1000 × 1400 artwork.
  let y = 59

  /*
   * ---------------------------------------------------------
   * PATIENT INFORMATION
   * ---------------------------------------------------------
   */

  const columnGap = 12
  const columnWidth = (contentWidth - columnGap) / 2

  function drawField(
    label: string,
    value: string,
    x: number,
    fieldY: number,
  ) {
    pdf.setTextColor(MUTED_GREEN)
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(7.5)

    pdf.text(label.toUpperCase(), x, fieldY)

    pdf.setTextColor(BRAND_DARK)
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(10)

    const valueLines = pdf.splitTextToSize(
      value || 'Not provided',
      columnWidth,
    ) as string[]

    pdf.text(valueLines, x, fieldY + 5)

    return fieldY + 5 + valueLines.length * 4
  }

  const patientFieldBottom = drawField(
    'Patient',
    patient.name,
    margin,
    y,
  )

  const dateFieldBottom = drawField(
    'Date',
    new Date(result.createdAt).toLocaleDateString('en-NG', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }),
    margin + columnWidth + columnGap,
    y,
  )

  y = Math.max(patientFieldBottom, dateFieldBottom) + 8

  const providerFieldBottom = drawField(
    'Healthcare Provider',
    provider?.name || 'Healthcare Provider',
    margin,
    y,
  )

  const serviceFieldBottom = drawField(
    'Service',
    appointment.service_type || 'Home Healthcare',
    margin + columnWidth + columnGap,
    y,
  )

  y = Math.max(providerFieldBottom, serviceFieldBottom) + 10

  /*
   * ---------------------------------------------------------
   * DIAGNOSIS
   * ---------------------------------------------------------
   */

  if (result.diagnosisText) {
    pdf.setTextColor(BRAND_GREEN)
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(8)

    pdf.text('DIAGNOSIS', margin, y)

    y += 5

    pdf.setTextColor(BRAND_DARK)
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(9.5)

    y = addWrappedText(
      pdf,
      result.diagnosisText,
      margin,
      y,
      contentWidth,
      4.5,
    )

    y += 8
  }

  /*
   * ---------------------------------------------------------
   * PRESCRIPTION
   * ---------------------------------------------------------
   */

  pdf.setTextColor(BRAND_GREEN)
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(8)

  pdf.text('PRESCRIPTION', margin, y)

  y += 7

  if (!result.prescriptions.length) {
    pdf.setTextColor(BRAND_DARK)
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(9.5)

    pdf.text('No prescription recorded.', margin, y)

    y += 10
  } else {
    result.prescriptions.forEach((prescription, index) => {
      /*
       * Drug name
       */
      pdf.setTextColor(BRAND_DARK)
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(9.5)

      const drugName = `${index + 1}. ${prescription.drugName}`

      const drugLines = pdf.splitTextToSize(
        drugName,
        contentWidth,
      ) as string[]

      pdf.text(drugLines, margin, y)

      y += drugLines.length * 4

      /*
       * Dosage / instructions
       */
      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(8.8)
      pdf.setTextColor('#36574F')

      y = addWrappedText(
        pdf,
        prescription.dosageInstructions,
        margin + 4,
        y + 1,
        contentWidth - 4,
        4,
      )

      /*
       * Subtle divider — intentionally much lighter than
       * the previous green prescription cards.
       */
      if (index < result.prescriptions.length - 1) {
        pdf.setDrawColor('#DCE5E1')
        pdf.setLineWidth(0.25)

        pdf.line(
          margin,
          y + 3,
          pageWidth - margin,
          y + 3,
        )

        y += 8
      } else {
        y += 5
      }
    })
  }

  /*
   * ---------------------------------------------------------
   * PROVIDER SIGNATURE
   * ---------------------------------------------------------
   */

  if (result.providerSignature) {
    const signatureX = pageWidth - margin
    const signatureY = pageHeight - 100

    pdf.setTextColor(BRAND_GREEN)
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(8)

    pdf.text('AUTHORIZED BY:', signatureX, signatureY, { align: 'right' })

    pdf.setTextColor(BRAND_DARK)
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(8.5)

    pdf.text(
      result.providerSignature,
      signatureX,
      signatureY + 6,
      { align: 'right' },
    )
  }

  /*
   * ---------------------------------------------------------
   * SAVE
   * ---------------------------------------------------------
   */

  const safeName =
    patient.name
      .replace(/[^a-z0-9]+/gi, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase() || 'patient'

  pdf.save(
    `homicareplus-result-${safeName}-${appointment.id.slice(0, 8)}.pdf`,
  )
}